import React, { createRef, useEffect, useRef, useState } from "react";

import "./valueDragPopUp.css";
import PopUp, { closePopUp } from "./popUp";
import Button from "./button";

interface ValueDragPopUpProps {
    title: string;
    options: string[];
    defaultActive: string;
    setActive: (v: string) => void;
}

export default function ValueDragPopUp(props: ValueDragPopUpProps) {

    const startY = useRef<number>(0);
    const [offsetY, setOffsetY] = useState(0);

    const [active, setActive] = useState(props.defaultActive);
    const [elHeight, setElHeight] = useState(0);

    const [touchStart, setTouchStart] = useState(0);

    const [manualSearch, setManualSearch] = useState("");
    const [manualSearchSuggestion, setManualSearchSuggestion] = useState("");

    var activePos = props.options.findIndex(option => option === active);
    const ulRef = createRef<HTMLUListElement>();
    const highlightRef = createRef<HTMLDivElement>();

    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionSpanRef = useRef<HTMLSpanElement>(null);
    const searchContainer = useRef<HTMLDivElement>(null);

    useEffect(() => {

        if(ulRef.current && highlightRef.current && searchContainer.current) {
            var children = ulRef.current.children;
            var firstChild = children[0] as HTMLLIElement;
            var elHeight = firstChild.offsetHeight;
            setElHeight(elHeight);
            var elWidth = firstChild.offsetWidth;
            var ulHeight = ulRef.current.offsetHeight;
            var marginTop = (elHeight * -1 * activePos + ulHeight / 2 - elHeight/2 + offsetY) + "px";
            firstChild.style.marginTop = marginTop;
            
            var highlight = highlightRef.current;
            highlight.style.width = elWidth + "px";
            highlight.style.height = elHeight + "px";
            highlight.style.marginInline = "calc(50% - " + (elWidth/2) + "px)";
            highlight.style.marginTop = ((ulHeight - elHeight)/2) + "px";

            var searchContainerEl = searchContainer.current;
            searchContainerEl.style.width = elWidth + "px";
            searchContainerEl.style.height = elHeight + "px";
            searchContainerEl.style.marginInline = "calc(50% - " + (elWidth/2) + "px)";
            searchContainerEl.style.marginTop = ((ulHeight - elHeight)/2) + "px";



            ulRef.current.addEventListener("mousedown", dragMouseDown);
            ulRef.current.addEventListener("wheel", onScroll as EventListener);
            ulRef.current.addEventListener("touchmove", onScroll as EventListener);
            ulRef.current.addEventListener("touchstart", onTouchStart);
            searchContainer.current.addEventListener("wheel", onScroll as EventListener);
            return () => {
                ulRef.current?.removeEventListener("mousedown", dragMouseDown);
                ulRef.current?.removeEventListener("wheel", onScroll as EventListener);
                ulRef.current?.removeEventListener("touchmove", onScroll as EventListener);
                ulRef.current?.removeEventListener("touchstart", onTouchStart);
                searchContainer.current?.removeEventListener("wheel", onScroll as EventListener);
            }
        }
    }, [active, offsetY]);

    function onTouchStart(e: TouchEvent) {
        setTouchStart(e.touches[0].clientY);
    }

    function onScroll(e: WheelEvent | TouchEvent) {
        var y = 0;
        var threshhold = 5;
        if(e instanceof WheelEvent) {
            y = e.deltaY;
        } else if(e instanceof TouchEvent) {
            threshhold = 30;
            if(e.touches.length > 0) {
                y = (e.changedTouches[0].clientY - touchStart) * -1;
                setTouchStart(e.touches[0].clientY);
            }
        }
        if(y > threshhold ) {
            // Scrolling up
            updateActiveRelative(1);
        }else if(y < -threshhold) {
            updateActiveRelative(-1);
        }
        setManualSearch("");
        e.preventDefault();
        e.stopPropagation();
    }

    function dragMouseDown(e: MouseEvent) {
        e = e || window.event;
        e.preventDefault();
        startY.current = e.clientY;
        document.body.addEventListener("mouseup", closeDragElement);
        document.body.addEventListener("mousemove", elementDrag);
    }

    function elementDrag(e: MouseEvent) {
        e = e || window.event;
        e.preventDefault();
        
        setOffsetY((startY.current - e.clientY) * -1);
    }

    function closeDragElement(e: MouseEvent) {
        e.stopPropagation();
        e.preventDefault();
        document.body.removeEventListener("mouseup", closeDragElement);
        document.body.removeEventListener("mousemove", elementDrag);
        setOffsetY(0);
    }

    function updateActive(e: string) {
        setActive(e);
        props.setActive(e);
    }

    function updateActiveRelative(amount: number) {
        const currentIndex = props.options.findIndex(option => option === active);
        const nextIndex = Math.max(0, currentIndex + amount);
        if(nextIndex > props.options.length-1) return;
        updateActive(props.options[nextIndex]);
    }

    const manualSearchTimeout = useRef<number>(-1);

    useEffect(() => {
        if(manualSearch.length > 0) {
            const foundOption = props.options.find(option => option.toLowerCase().startsWith(manualSearch.toLowerCase()));
            if(foundOption) {
                updateActive(foundOption);
                setManualSearchSuggestion(foundOption);
            }
            clearTimeout(manualSearchTimeout.current);
            if(manualSearch != "") {
                manualSearchTimeout.current = window.setTimeout(() => {
                    setManualSearch("");
                }, 50000);
            }
        }
    }, [manualSearch]);

    useEffect(() => {
        if(inputRef.current && suggestionSpanRef.current) {
            var suggestionWidth = suggestionSpanRef.current.offsetWidth;
            console.log(suggestionWidth);
            inputRef.current.style.paddingRight = suggestionWidth + "px";
        }
    }, [manualSearchSuggestion, manualSearch])


    const handleBlur = () => {
        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    };

    useEffect(() => {
        inputRef.current?.focus();
        
    }, []);

    return <PopUp title={props.title} subtitle="or type in a value">
        <div ref={highlightRef} className="value-drag-highlight"/>
        <div ref={searchContainer} className={"autocomplete-container value-drag-search-container" + (manualSearch.length == 0 ? " invisible" : "")}>
            <div className="autocomplete-suggestion" aria-hidden="true">
                <span className="autocomplete-suggestion-invisible article-font-size no-style">{manualSearch}</span>
                <span ref={suggestionSpanRef} className="autocomplete-suggestion-visible article-font-size no-style">
                    {manualSearchSuggestion.slice(manualSearch.length)}
                </span>
            </div>
            <input onBlur={handleBlur} className={"value-drag-search manual-search article-font-size" + (manualSearch.length == 0 ? " invisible" : "")} ref={inputRef} type="text" value={manualSearch} onChange={(e) => {
                setManualSearch(e.target.value);
            }}/>
        </div>

        <div className="value-drag-wrapper">
            <ul ref={ulRef} className="value-drag">
                {props.options.map((e, i) => {
                    return <li className={active == e ? "active" : ""} onClick={() => {
                        updateActive(e);
                        setManualSearch("");
                    }}><a className="unselectable">{e}</a></li>
                })}
            </ul>
        </div>
        <Button onClick={closePopUp}>Save</Button>
    </PopUp>
}